import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { MdClear, MdDelete, MdEdit } from "react-icons/md";
import { FaCirclePlus } from "react-icons/fa6";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Select from "react-select";

import Container from "../../components/Container";
import Loading from "../../../home/components/Loading";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import DatePicker from "../../../../components/Input/DatePicker";
import styles from "./styles.module.scss";

const selectPageStyle = {
  control: styles => ({
    ...styles,
    backgroundColor: "inherit",
    width: "100%",
    border: "none",
    boxShadow: "none",
    outline: "none",
    color: "#807E80",
    padding: "0",
    cursor: "pointer",
    maxHeight: "48px"
  }),
  container: styles => ({
    ...styles,
    minWidth: "56px",
    outline: "none",
    borderBottom: "1px solid #4D4D4D",
    padding: "0",
    cursor: "pointer"
  }),
  indicatorSeparator: styles => ({
    ...styles,
    display: "none",
    border: "none"
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    fontSize: "14px",
    fontFamily: "Mulish",
    fontWeight: 400,
    color: isSelected ? "#fff" : "#807E80",
    "&:hover": {
      backgroundColor: "#c1c1c1",
      color: "#333333",
      cursor: "pointer"
    },
    backgroundColor: isSelected ? "#409e6a" : "#fff"
  }),
  valueContainer: styles => ({ ...styles, padding: "0" }),
  dropdownIndicator: styles => ({ ...styles, padding: "0", color: "#4D4D4D" })
};

const perPageOptions = [
  { label: "10", value: 10 },
  { label: "15", value: 15 },
  { label: "20", value: 20 },
  { label: "50", value: 50 }
];

const HolidaysPage = () => {
  window.setPageTitle("Gestão de Feriados");

  const api = new BrComerceApiService();

  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("nacionais");

  const [nationalHolidays, setNationalHolidays] = useState([]);
  const [regionalHolidays, setRegionalHolidays] = useState([]);
  const [easterHolidays, setEasterHolidays] = useState([]);

  const [filteredHolidays, setFilteredHolidays] = useState([]);
  const [searchString, setSearchString] = useState("");

  const [actualPage, setActualPage] = useState(1);
  const [resultsPages, setResultsPages] = useState({ label: "10", value: 10 });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [newHoliday, setNewHoliday] = useState({
    name: "",
    date: null,
    uf: ""
  });

  const loadHolidays = useCallback(async () => {
    try {
      setLoading(true);

      const [dbResponse, easterResponse] = await Promise.all([
        api.makeHttpRequest({ method: "GET", url: "/holidays" }),
        api.makeHttpRequest({
          method: "GET",
          url: `/holidays/easter?year=${new Date().getFullYear()}`
        })
      ]);

      const dbData = dbResponse?.data || dbResponse || [];
      const easterData = easterResponse?.data || easterResponse || [];

      const nacionais = dbData.filter(h => !h.uf);
      const regionais = dbData.filter(h => !!h.uf);

      setNationalHolidays(nacionais);
      setRegionalHolidays(regionais);
      setEasterHolidays(easterData);

      setFilteredHolidays(nacionais);
      setActiveTab("nacionais");
      setSearchString("");
      setActualPage(1);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Houve um erro ao carregar os feriados."
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    let currentList = [];

    if (activeTab === "nacionais") currentList = nationalHolidays;
    if (activeTab === "regionais") currentList = regionalHolidays;
    if (activeTab === "pascoa") currentList = easterHolidays;

    if (!searchString) {
      setFilteredHolidays(currentList);
    } else {
      const lowerSearch = searchString.toLowerCase();
      const filtered = currentList.filter(
        h =>
          h.name.toLowerCase().includes(lowerSearch) ||
          (h.uf && h.uf.toLowerCase().includes(lowerSearch))
      );
      setFilteredHolidays(filtered);
    }
    setActualPage(1);
  }, [
    searchString,
    activeTab,
    nationalHolidays,
    regionalHolidays,
    easterHolidays
  ]);

  const handleTabChange = tabName => {
    setActiveTab(tabName);
    setSearchString("");
    setShowForm(false);
    setEditingId(null);
  };

  const handleDelete = async id => {
    Swal.fire({
      title: "Você tem certeza?",
      html: "Deseja realmente excluir este feriado?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ca1818",
      cancelButtonColor: "#c1c1c1",
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar"
    }).then(async response => {
      if (response?.value) {
        try {
          setLoading(true);
          await api.makeHttpRequest({
            method: "DELETE",
            url: `/holidays/${id}`
          });
          Swal.fire("Excluído!", "Feriado removido com sucesso.", "success");
          loadHolidays();
        } catch (e) {
          Swal.fire(
            "Erro",
            "Erro ao excluir feriado. Verifique o console.",
            "error"
          );
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleEdit = holiday => {
    const start = holiday.date ? holiday.date.split("T")[0] : null;

    setNewHoliday({
      name: holiday.name,
      date: start,
      uf: holiday.uf || ""
    });

    setEditingId(holiday.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggleForm = () => {
    if (showForm) {
      setShowForm(false);
      setEditingId(null);
      setNewHoliday({ name: "", date: null, uf: "" });
    } else {
      if (activeTab === "pascoa") setActiveTab("nacionais");
      setShowForm(true);
    }
  };

  const handleOnChangeCalendar = e => {
    if (!e) {
      setNewHoliday(prev => ({ ...prev, date: null }));
      return;
    }

    if (e instanceof Date && !isNaN(e)) {
      const ano = e.getFullYear();
      const mes = String(e.getMonth() + 1).padStart(2, "0");
      const dia = String(e.getDate()).padStart(2, "0");
      setNewHoliday(prev => ({ ...prev, date: `${ano}-${mes}-${dia}` }));
      return;
    }

    let dateString = "";

    if (typeof e === "string") {
      dateString = e;
    } else if (Array.isArray(e) && e.length > 0) {
      dateString = e[0];
    } else if (e && typeof e === "object") {
      dateString =
        e.dateStart ||
        e.date ||
        e.startDate ||
        e.value ||
        (e.target && e.target.value);
    }

    if (typeof dateString === "string") {
      if (dateString.includes("/")) {
        const [dia, mes, ano] = dateString.substring(0, 10).split("/");
        if (ano && mes && dia) {
          setNewHoliday(prev => ({ ...prev, date: `${ano}-${mes}-${dia}` }));
        }
      } else if (dateString.includes("-")) {
        setNewHoliday(prev => ({ ...prev, date: dateString.substring(0, 10) }));
      }
    }
  };
  const handleClearCalendar = () => {
    setNewHoliday(prev => ({ ...prev, date: null }));
  };

  const handleCreateSubmit = async e => {
    e.preventDefault();
    if (!newHoliday.date) {
      Swal.fire(
        "Atenção",
        "Por favor, selecione a data do feriado no calendário.",
        "warning"
      );
      return;
    }

    if (activeTab === "regionais" && !newHoliday.uf) {
      Swal.fire(
        "Atenção",
        "Para cadastrar um feriado regional, informe a UF.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: newHoliday.name,
        date: newHoliday.date + "T12:00:00"
      };

      if (newHoliday.uf) {
        payload.uf = newHoliday.uf.toUpperCase();
      }

      if (editingId) {
        await api.makeHttpRequest({
          method: "PUT",
          url: `/holidays/${editingId}`,
          data: payload
        });
        Swal.fire("Sucesso!", "Feriado atualizado com sucesso.", "success");
      } else {
        await api.makeHttpRequest({
          method: "POST",
          url: "/holidays",
          data: payload
        });
        Swal.fire("Sucesso!", "Feriado cadastrado.", "success");
      }

      setNewHoliday({ name: "", date: null, uf: "" });
      setEditingId(null);
      setShowForm(false);
      loadHolidays();
    } catch (e) {
      console.error("Erro ao salvar:", e);
      Swal.fire("Erro", "Falha ao salvar o feriado.", "error");
    } finally {
      setLoading(false);
    }
  };

  const indexOfLastItem = actualPage * resultsPages.value;
  const indexOfFirstItem = indexOfLastItem - resultsPages.value;
  const currentItems = filteredHolidays.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages =
    Math.ceil(filteredHolidays.length / resultsPages.value) || 1;

  return (
    <>
      <Loading isLoading={loading} />

      <Container title="Gestão de Feriados">
        <div className={styles.contentBody}>
          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tabBtn} ${
                activeTab === "nacionais" ? styles.activeTab : ""
              }`}
              onClick={() => handleTabChange("nacionais")}
            >
              Feriados Nacionais
            </button>
            <button
              className={`${styles.tabBtn} ${
                activeTab === "regionais" ? styles.activeTab : ""
              }`}
              onClick={() => handleTabChange("regionais")}
            >
              Feriados Regionais
            </button>
            <button
              className={`${styles.tabBtn} ${
                activeTab === "pascoa" ? styles.activeTab : ""
              }`}
              onClick={() => handleTabChange("pascoa")}
            >
              Feriados (Páscoa)
            </button>
          </div>

          <div className={styles.filters}>
            <div className={styles.filterInput}>
              <div className={styles.input}>
                <input
                  type="text"
                  placeholder="Filtre pelo nome ou UF do feriado..."
                  onChange={e => setSearchString(e.target.value)}
                  value={searchString}
                />
                {!!searchString.length && (
                  <MdClear
                    style={{
                      cursor: "pointer",
                      color: "#807e80",
                      marginLeft: "8px"
                    }}
                    onClick={() => setSearchString("")}
                  />
                )}
              </div>
            </div>

            <button
              type="button"
              className={styles.btnAddRange}
              onClick={handleToggleForm}
            >
              {showForm ? (
                <MdClear
                  fill="#fff"
                  color="#fff"
                  style={{ fontSize: "18px" }}
                />
              ) : (
                <FaCirclePlus fill="#fff" color="#fff" />
              )}
              <span>
                {showForm
                  ? editingId
                    ? "Cancelar Edição"
                    : "Cancelar Cadastro"
                  : "Criar novo feriado"}
              </span>
            </button>
          </div>

          {showForm && (
            <form className={styles.createForm} onSubmit={handleCreateSubmit}>
              <div className={styles.input} style={{ flex: 1.5 }}>
                <input
                  type="text"
                  required
                  placeholder="Nome do Feriado"
                  value={newHoliday.name}
                  onChange={e =>
                    setNewHoliday({ ...newHoliday, name: e.target.value })
                  }
                />
              </div>

              <div
                className={`${styles.input} ${styles.selectPicker}`}
                style={{ flex: 2 }}
              >
                <DatePicker
                  key={editingId ? `edit-${editingId}` : "new-holiday"}
                  className={`datePickerConfig ${styles.inputPicker}`}
                  change={handleOnChangeCalendar}
                  placeholder="Selecione a data"
                  showClearButton={!!newHoliday.date}
                  onClearFields={handleClearCalendar}
                  defaultValue={
                    newHoliday.date
                      ? new Date(`${newHoliday.date}T12:00:00`)
                      : null
                  }
                />
              </div>
              <div className={styles.input} style={{ flex: 0.8 }}>
                <input
                  type="text"
                  placeholder="UF (Vazio = Nacional)"
                  maxLength={2}
                  value={newHoliday.uf}
                  onChange={e =>
                    setNewHoliday({ ...newHoliday, uf: e.target.value })
                  }
                />
              </div>
              <button type="submit" className={styles.btnSaveForm}>
                {editingId ? "Atualizar Feriado" : "Salvar Feriado"}
              </button>
            </form>
          )}

          <div className={styles.contentTable}>
            <table className={styles.tableContainer}>
              <thead>
                <tr>
                  <th width="40%">Nome do Feriado</th>
                  <th width="25%">Data</th>
                  <th width="20%">Abrangência (UF)</th>
                  {activeTab !== "pascoa" && <th width="15%">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0
                  ? currentItems.map(holiday => (
                      <tr key={holiday.id}>
                        <td>{holiday.name}</td>
                        <td>{moment.utc(holiday.date).format("DD/MM/YYYY")}</td>
                        <td>
                          {holiday.uf ? `Estadual (${holiday.uf})` : "Nacional"}
                        </td>

                        {activeTab !== "pascoa" && (
                          <td className={styles.actionsWrapper}>
                            <button
                              type="button"
                              onClick={() => handleEdit(holiday)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                marginRight: "10px"
                              }}
                              title="Editar"
                            >
                              <MdEdit size={20} color="#409E6A" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(holiday.id)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer"
                              }}
                              title="Excluir"
                            >
                              <MdDelete size={20} color="#ca1818" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  : !loading && (
                      <tr>
                        <td
                          colSpan={activeTab !== "pascoa" ? "4" : "3"}
                          style={{
                            textAlign: "center",
                            padding: "30px",
                            color: "#807e80"
                          }}
                        >
                          Nenhum feriado encontrado nesta categoria.
                        </td>
                      </tr>
                    )}
              </tbody>
            </table>

            <div className={styles.paginationContainer}>
              <div className={styles.perPageContent}>
                <span>Itens por página:</span>
                <Select
                  styles={selectPageStyle}
                  value={resultsPages}
                  options={perPageOptions}
                  onChange={e => {
                    setResultsPages(e);
                    setActualPage(1);
                  }}
                  menuPlacement="top"
                />
              </div>
              <h6>
                {actualPage} - {totalPages} de {totalPages} páginas
              </h6>
              <div className={styles.controlPage}>
                <FiChevronLeft
                  size={25}
                  onClick={() =>
                    actualPage > 1 && setActualPage(actualPage - 1)
                  }
                  color={actualPage > 1 ? "#409E6A" : "#2D724C"}
                  className="arrow"
                />
                <FiChevronRight
                  size={25}
                  onClick={() =>
                    actualPage < totalPages && setActualPage(actualPage + 1)
                  }
                  color={actualPage < totalPages ? "#409E6A" : "#2D724C"}
                  className="arrow"
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default HolidaysPage;
